import type {
  BettingEventWithMarkets,
  ProcessedBettingEvent,
  ProcessedBettingCompetitors,
  ProcessedMarkets,
  ProcessedOutcome,
} from "@/hooks/firestore/betting/useEvent";
import type {
  ProcessedRacingEvent,
  ProcessedRacingCompetitors,
} from "@/hooks/firestore/betting/useRacingEvents";
import type { QuaddieMarket } from "@/hooks/firestore/betting/useQuaddieMarkets";
import type { MysteryMulti } from "@/features/sem-generator/util/constants";
import type {
  BetOrigin,
  BetslipHub,
  QuaddieSelections,
  QuaddieType,
  SportCompetitor,
  RacingCompetitor,
} from "@/features/betslip/types";

/**
 * Market shape, derived from what the processed-event hooks return.
 * Both betting and racing events share the same processed market shape
 * (markets are processed by the same `processMarket` helper for either
 * hub).
 */
export type ProcessedMarket = ProcessedMarkets[number];

export type { ProcessedBettingEvent, ProcessedRacingEvent, ProcessedOutcome };

/** Per-leg payload for SEM bets. */
export type SemSubSelectionInput = {
  outcomeId: string;
  marketId: string;
  name: string;
  marketName?: string;
  outcomeType?: string;
  marketType?: string;
  /** Per-leg odds — used by token-context `checkMinimumOutcomeOdds`. */
  odds?: number;
  competitor?: SportCompetitor | RacingCompetitor;
  competitorId?: string;
  outcomeGroup?: string;
};

/** Per-leg payload for exotic bets (Quinella / Exacta / Trifecta / First 4). */
export type ExoticSubSelectionInput = {
  outcomeId: string;
  marketId: string;
  name: string;
  marketName?: string;
  /** Position discriminator: RUNNER_WIN / RUNNER_POS_2 / RUNNER_TOP_2 etc. */
  outcomeType?: string;
  attributes?: Record<string, string>;
  competitor?: RacingCompetitor;
};

type Base = {
  originType?: BetOrigin;
  originId?: string;
};

/**
 * Sport events come from `useBettingEvent` (list pages) or
 * `useBettingEventWithMarkets` (event pages, fully hydrated). Both
 * satisfy what the builder + telemetry reads.
 */
type SportEvent = ProcessedBettingEvent | BettingEventWithMarkets;

export type MatchToggleInput = Base & {
  kind: "match";
  event: SportEvent;
  market: ProcessedMarket;
  outcome: ProcessedOutcome;
  outcomeId: string;
  hub: "sports" | "esports";
};

export type OutrightToggleInput = Base & {
  kind: "outright";
  event: SportEvent;
  market: ProcessedMarket;
  outcome: ProcessedOutcome;
  outcomeId: string;
  hub: "sports" | "esports";
};

export type RaceToggleInput = Base & {
  kind: "race";
  event: ProcessedRacingEvent;
  market: ProcessedMarket;
  outcome: ProcessedOutcome;
  outcomeId: string;
  hub: "racing";
  timezone?: string;
  markets: ProcessedMarkets;
  competitors: ProcessedRacingCompetitors;
};

export type SemToggleInput = Base & {
  kind: "sem";
  event: SportEvent | ProcessedRacingEvent;
  hub: BetslipHub;
  /** SEM's own outcomeId — caller generates (typically `uuid.v4()`). */
  outcomeId: string;
  /** Combined SEM odds. */
  odds: number;
  subSelections: SemSubSelectionInput[];
  markets: ProcessedMarkets;
  competitors: ProcessedRacingCompetitors | ProcessedBettingCompetitors;
  builder?: "sem_generator" | "sem_builder";
  mysteryMulti?: MysteryMulti;
};

export type ExoticToggleInput = Base & {
  kind: "exotic";
  event: ProcessedRacingEvent;
  market: ProcessedMarket;
  hub: "racing";
  /** Exotic's own outcomeId — caller generates. */
  outcomeId: string;
  subSelections: ExoticSubSelectionInput[];
  markets: ProcessedMarkets;
  competitors: ProcessedRacingCompetitors;
};

export type QuaddieToggleInput = Base & {
  kind: "quaddie";
  event: ProcessedRacingEvent;
  market: QuaddieMarket;
  hub: "racing";
  /** Quaddie's own outcomeId — caller generates. */
  outcomeId: string;
  quaddieSelections: QuaddieSelections;
  quaddieType: QuaddieType;
  markets: ProcessedMarkets;
  competitors: ProcessedRacingCompetitors;
};

/**
 * Runtime guard for racing vs sport events. `eventType === "RACE"` is
 * stamped on by `processRacingEvent`; sport events carry their firestore
 * eventType (`"MATCH"` / `"OUTRIGHT"` / ...).
 */
export const isRacingEvent = (
  event: SportEvent | ProcessedRacingEvent,
): event is ProcessedRacingEvent => event.eventType === "RACE";

/**
 * The single input shape `useToggleSelection().toggle()` accepts. A
 * discriminated union keyed on `kind`; TS picks the right branch and
 * enforces per-kind constraints (e.g. race must carry a
 * `ProcessedRacingEvent`).
 */
export type ToggleSelectionInput =
  | MatchToggleInput
  | OutrightToggleInput
  | RaceToggleInput
  | SemToggleInput
  | ExoticToggleInput
  | QuaddieToggleInput;
